import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  X, Mail, Phone, Building2, Calendar, UserPlus, FileText, Loader2,
  CheckCircle2, ExternalLink, ClipboardList, MessageSquare, Plus, Globe,
  PhoneCall, PhoneOff, PhoneMissed, Voicemail, Ban, RotateCcw, Target,
  BookMarked, Save, Trash2,
} from "lucide-react";
import { api } from "../../lib/api";
import HealthQuestionnaire from "./HealthQuestionnaire";
import { TIMEZONES, detectTimezone, wallTimeToIso } from "../../lib/timezones";

const CALL_OUTCOMES = [
  { id: "connected",          label: "Connected",         icon: PhoneCall,   tone: "bg-[#6b7c3a]/12 text-[#4d5a24]" },
  { id: "voicemail",          label: "Voicemail",         icon: Voicemail,   tone: "bg-[#c9a961]/18 text-[#a3823f]" },
  { id: "no_answer",          label: "No answer",         icon: PhoneMissed, tone: "bg-black/8 text-black/55" },
  { id: "callback_requested", label: "Callback requested", icon: RotateCcw,  tone: "bg-[#8a4b7a]/12 text-[#8a4b7a]" },
  { id: "wrong_number",       label: "Wrong number",      icon: PhoneOff,    tone: "bg-[#b23a48]/10 text-[#b23a48]" },
  { id: "not_interested",     label: "Not interested",    icon: Ban,         tone: "bg-[#b23a48]/10 text-[#b23a48]" },
];

const STATUS_OPTIONS = [
  { id: "new",     label: "New",     tone: "bg-black/6 text-black/60" },
  { id: "nurture", label: "Nurture", tone: "bg-[#c9a961]/20 text-[#a3823f]" },
  { id: "won",     label: "Won",     tone: "bg-[#6b7c3a]/15 text-[#4d5a24]" },
  { id: "lost",    label: "Lost",    tone: "bg-[#b23a48]/10 text-[#b23a48]" },
];

const DEFAULT_RECORDS = [
  "Last 3 months of business bank statements (PDF or CSV)",
  "Most recent Profit & Loss statement",
  "Current vendor / subscription list with monthly amounts",
  "Last two months of merchant processing statements",
  "Any open BBB complaints or dispute correspondence",
];

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

/**
 * Slide-in drawer that shows a small profile for a lead and exposes
 * three primary conversions: promote to CRM, request records, book slot.
 */
export default function LeadProfileDrawer({ lead, secret, onClose, onConverted, onBooked, onCallLogged, onStatusChanged }) {
  const [mode, setMode] = useState("view"); // "view" | "records" | "book" | "call"
  const [busy, setBusy] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  // Records checklist state — the admin can uncheck items they don't need,
  // and append their own custom entries via the input at the bottom.
  const [checklist, setChecklist] = useState(
    Object.fromEntries(DEFAULT_RECORDS.map((x) => [x, true])),
  );
  const [customItems, setCustomItems] = useState([]); // [{ text, checked }]
  const [newCustom, setNewCustom] = useState("");
  const [note, setNote] = useState("");
  // Records-request templates — reusable checklists founders can save + drop in.
  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Tracks the template currently loaded into the checklist — enables the
  // "Update <name>" affordance so editing a loaded template doesn't always
  // spawn a new copy in the picker.
  const [loadedTemplate, setLoadedTemplate] = useState(null);
  // Book-appointment state — split date + time so we can honour the timezone.
  const [bookDate, setBookDate] = useState("");
  const [bookTime, setBookTime] = useState("");
  const [bookTz, setBookTz] = useState(() => detectTimezone());
  const [goal, setGoal] = useState("");
  // Call-log state
  const [callOutcome, setCallOutcome] = useState("");
  const [callNotes, setCallNotes] = useState("");
  const [callDuration, setCallDuration] = useState("");
  const [callBy, setCallBy] = useState(() => {
    try { return localStorage.getItem("elevare_transfer_approver") || "Kalib"; }
    catch { return "Kalib"; }
  });

  // Lazy-load templates the first time the records panel opens.
  useEffect(() => {
    if (!lead || mode !== "records" || templatesLoaded) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/records-request-templates?secret=${encodeURIComponent(secret || "")}`);
        setTemplates(data?.templates || []);
      } catch {
        /* silent — templates are optional */
      } finally {
        setTemplatesLoaded(true);
      }
    })();
  }, [lead, mode, templatesLoaded, secret]);

  if (!lead) return null;

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    const items = Array.isArray(tpl.items) ? tpl.items : [];
    const nextChecklist = Object.fromEntries(DEFAULT_RECORDS.map((x) => [x, false]));
    const extras = [];
    items.forEach((raw) => {
      const it = (raw || "").trim();
      if (!it) return;
      if (nextChecklist[it] !== undefined) {
        nextChecklist[it] = true;
      } else {
        extras.push({ text: it, checked: true });
      }
    });
    setChecklist(nextChecklist);
    setCustomItems(extras);
    setLoadedTemplate(tpl);
    toast.success(`Loaded template “${tpl.name}”.`);
  };

  const collectCurrentItems = () => {
    const defaults = Object.entries(checklist).filter(([, v]) => v).map(([k]) => k);
    const extras = customItems.filter((c) => c.text.trim()).map((c) => c.text.trim());
    return [...defaults, ...extras];
  };

  // Save the current checklist as a NEW reusable template.
  const saveTemplate = async () => {
    const items = collectCurrentItems();
    if (items.length === 0) {
      toast.error("Pick at least one item before saving as a template.");
      return;
    }
    const name = window.prompt("Name this template (e.g. “Retail intake · Q4”)");
    if (!name || !name.trim()) return;
    const category = (window.prompt(
      "Group under which industry? (retail, restaurant, franchise, saas, medical, general …)",
      loadedTemplate?.category || "general",
    ) || "general").trim().toLowerCase();
    setSavingTemplate(true);
    try {
      const { data } = await api.post("/admin/records-request-templates",
        { secret, name: name.trim(), items, category });
      setTemplates((prev) => [data, ...prev]);
      setLoadedTemplate(data);  // treat the new template as the loaded one
      toast.success(`Saved template “${data.name}” to ${data.category}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template.");
    } finally {
      setSavingTemplate(false);
    }
  };

  // Update the currently-loaded template in-place with the current checklist.
  // Uses the PATCH endpoint added in the categories work — same doc, new items.
  const updateLoadedTemplate = async () => {
    if (!loadedTemplate?.id) return;
    const items = collectCurrentItems();
    if (items.length === 0) {
      toast.error("Pick at least one item before updating this template.");
      return;
    }
    setSavingTemplate(true);
    try {
      const { data } = await api.patch(
        `/admin/records-request-templates/${loadedTemplate.id}`,
        { secret, name: loadedTemplate.name, items, category: loadedTemplate.category || "general" },
      );
      setTemplates((prev) => prev.map((t) => (t.id === data.id ? data : t)));
      setLoadedTemplate(data);
      toast.success(`Updated “${data.name}”.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update template.");
    } finally {
      setSavingTemplate(false);
    }
  };

  const recategorizeTemplate = async (tpl) => {
    if (!tpl?.id) return;
    const category = (window.prompt(
      `Move “${tpl.name}” to which category?`,
      tpl.category || "general",
    ) || "").trim().toLowerCase();
    if (!category || category === tpl.category) return;
    try {
      const { data } = await api.patch(
        `/admin/records-request-templates/${tpl.id}`,
        { secret, name: tpl.name, items: tpl.items, category },
      );
      setTemplates((prev) => prev.map((t) => (t.id === tpl.id ? data : t)));
      toast.success(`Moved to ${data.category}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not move template.");
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!tpl?.id) return;
    if (!window.confirm(`Delete template “${tpl.name}”?`)) return;
    try {
      await api.delete(`/admin/records-request-templates/${tpl.id}?secret=${encodeURIComponent(secret || "")}`);
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      if (loadedTemplate?.id === tpl.id) setLoadedTemplate(null);
      toast.success("Template deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete template.");
    }
  };

  const displayName = lead.name || lead.email || "Lead";
  const initials = (displayName || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const convert = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/convert`,
        { secret, send_portal_invite: true });
      const msg = data?.already_existed
        ? `Already in CRM — record refreshed for ${lead.email}.`
        : `Added to CRM · portal invite ${data?.portal_invite_sent ? "sent" : "skipped"}.`;
      toast.success(msg);
      onConverted?.(lead.id, data?.vault);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not convert this lead.");
    } finally {
      setBusy(false);
    }
  };

  const requestRecords = async () => {
    if (busy) return;
    const defaults = Object.entries(checklist).filter(([, v]) => v).map(([k]) => k);
    const extras = customItems.filter((c) => c.checked && c.text.trim()).map((c) => c.text.trim());
    const items = [...defaults, ...extras];
    if (items.length === 0) {
      toast.error("Pick or add at least one item to request.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/request-records`,
        { secret, items, note: note || undefined });
      toast.success(`Records request emailed to ${data?.sent_to}.`);
      setMode("view");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the request.");
    } finally {
      setBusy(false);
    }
  };

  const addCustomItem = () => {
    const text = newCustom.trim();
    if (!text) return;
    if (text.length > 200) {
      toast.error("Keep the record request under 200 characters.");
      return;
    }
    // Avoid dupes (case-insensitive) against defaults and existing custom rows.
    const lower = text.toLowerCase();
    const dupe = DEFAULT_RECORDS.some((d) => d.toLowerCase() === lower)
      || customItems.some((c) => c.text.trim().toLowerCase() === lower);
    if (dupe) {
      toast.error("That request is already in the list.");
      return;
    }
    setCustomItems([...customItems, { text, checked: true }]);
    setNewCustom("");
  };

  const changeStatus = async (nextStatus) => {
    if (busy || nextStatus === (lead.status || "new")) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/leads/${lead.id}/status`,
        { secret, status: nextStatus });
      onStatusChanged?.(lead.id, data);
      toast.success(`Marked ${nextStatus}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status.");
    } finally {
      setBusy(false);
    }
  };

  const saveCallLog = async () => {
    if (busy) return;
    if (!callOutcome) {
      toast.error("Pick an outcome first.");
      return;
    }
    const dur = callDuration.trim() ? parseInt(callDuration, 10) : undefined;
    if (callDuration.trim() && (isNaN(dur) || dur <= 0)) {
      toast.error("Duration must be a positive number of minutes.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/call-log`, {
        secret,
        outcome: callOutcome,
        notes: callNotes.trim() || undefined,
        by: callBy || undefined,
        duration_min: dur,
      });
      toast.success(`Call logged — ${callOutcome.replace("_", " ")}.`);
      onCallLogged?.(lead.id, data?.entry, data?.last_call_outcome);
      // Reset composer, drop back to view so the fresh log is visible.
      setCallOutcome("");
      setCallNotes("");
      setCallDuration("");
      setMode("view");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the call log.");
    } finally {
      setBusy(false);
    }
  };

  const bookAppointment = async () => {
    if (busy) return;
    if (!bookDate || !bookTime) {
      toast.error("Pick a date and time first.");
      return;
    }
    const iso = wallTimeToIso(bookDate, bookTime, bookTz);
    if (!iso) {
      toast.error("That date/time doesn't look valid.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings`, {
        secret,
        slot_iso: iso,
        name: lead.name || "",
        email: lead.email,
        company: lead.company || undefined,
        goal: goal || lead.message || undefined,
      });
      toast.success(`Booked · ${fmtWhen(data?.slot_iso || iso)}. Confirmation emailed.`);
      onBooked?.(data);
      setMode("view");
      setBookDate("");
      setBookTime("");
      setGoal("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not book that slot.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="lead-profile-drawer"
      className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/30 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <aside
        className="w-full sm:w-[440px] bg-[#faf7ef] shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300"
        role="dialog"
        aria-label={`Lead profile for ${displayName}`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#1e3d3a]/10 bg-[#faf7ef]/95 backdrop-blur px-5 py-4">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Lead profile</p>
          <button
            type="button"
            data-testid="lead-drawer-close"
            onClick={onClose}
            className="text-[#1e3d3a]/50 hover:text-[#b23a48] transition-colors duration-300"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Profile card */}
        <div className="px-5 py-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-[#1e3d3a] text-[#c9a961] grid place-items-center font-head text-lg">
              {initials || "?"}
            </div>
            <div className="min-w-0">
              <h3 className="font-head text-xl text-[#1e3d3a] truncate">{displayName}</h3>
              {lead.company && (
                <p className="text-xs text-[#1e3d3a]/60 flex items-center gap-1 truncate">
                  <Building2 className="h-3 w-3" /> {lead.company}
                </p>
              )}
            </div>
          </div>

          {/* Contact facts */}
          <div className="rounded-xl border border-[#1e3d3a]/10 bg-white divide-y divide-[#1e3d3a]/6 text-xs">
            <div className="flex items-center gap-3 px-3 py-2.5">
              <Mail className="h-3.5 w-3.5 text-[#a3823f] shrink-0" />
              <a
                href={`mailto:${lead.email}`}
                data-testid="lead-drawer-email"
                className="text-[#1e3d3a] hover:text-[#a3823f] transition-colors duration-300 truncate"
              >
                {lead.email}
              </a>
            </div>
            {lead.phone && (
              <div className="flex items-center gap-3 px-3 py-2.5">
                <Phone className="h-3.5 w-3.5 text-[#a3823f] shrink-0" />
                <a
                  href={`tel:${lead.phone}`}
                  data-testid="lead-drawer-phone"
                  className="text-[#1e3d3a] hover:text-[#a3823f] transition-colors duration-300"
                >
                  {lead.phone}
                </a>
              </div>
            )}
            <div className="flex items-center gap-3 px-3 py-2.5">
              <Calendar className="h-3.5 w-3.5 text-[#a3823f] shrink-0" />
              <span className="text-[#1e3d3a]/70">Received {fmtWhen(lead.created_at)}</span>
            </div>
            {(lead.added_by || lead.added_at) && (
              <div className="flex items-center gap-3 px-3 py-2.5" data-testid="lead-drawer-added-by">
                <UserPlus className="h-3.5 w-3.5 text-[#a3823f] shrink-0" />
                <span className="text-[#1e3d3a]/70">
                  Added by <strong className="text-[#1e3d3a]">{lead.added_by || "web form"}</strong>
                  {lead.added_at && <> · {fmtWhen(lead.added_at)}</>}
                </span>
              </div>
            )}
            {lead.source && (
              <div className="flex items-center gap-3 px-3 py-2.5">
                <ClipboardList className="h-3.5 w-3.5 text-[#a3823f] shrink-0" />
                <span className="text-[#1e3d3a]/70">Source: {lead.source}</span>
              </div>
            )}
          </div>

          {/* Message */}
          {(lead.message || lead.notes) && (
            <div className="rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/6 px-3 py-3">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-1 flex items-center gap-1">
                <MessageSquare className="h-3 w-3" /> Their message
              </p>
              <p className="text-xs text-[#1e3d3a] leading-relaxed whitespace-pre-wrap">
                {lead.message || lead.notes}
              </p>
            </div>
          )}

          {/* Status chips */}
          <div className="flex flex-wrap gap-1.5">
            {lead.converted_client_id && (
              <span
                data-testid="lead-drawer-converted-chip"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] bg-[#6b7c3a]/12 text-[#4d5a24] px-2.5 py-1 rounded-full"
              >
                <CheckCircle2 className="h-3 w-3" /> Now in CRM
              </span>
            )}
            {lead.records_requested_at && (
              <span
                data-testid="lead-drawer-records-chip"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] bg-[#c9a961]/18 text-[#a3823f] px-2.5 py-1 rounded-full"
                title={`Requested ${fmtWhen(lead.records_requested_at)}`}
              >
                <FileText className="h-3 w-3" /> Records requested
              </span>
            )}
          </div>

          {/* Outcome selector — won/lost/nurture */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] mb-1.5">Outcome</p>
            <div className="flex flex-wrap gap-1.5" data-testid="lead-drawer-status">
              {STATUS_OPTIONS.map((s) => {
                const current = (lead.status || (lead.converted_client_id ? "won" : "new"));
                const active = current === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`lead-status-${s.id}`}
                    onClick={() => changeStatus(s.id)}
                    disabled={busy}
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full transition-all duration-300 ${
                      active
                        ? "bg-[#1e3d3a] text-white ring-2 ring-[#c9a961]"
                        : `${s.tone} hover:brightness-95`
                    }`}
                  >
                    <Target className="h-3 w-3" /> {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Action panel */}
        {mode === "view" && (
          <div className="px-5 pb-8 space-y-2">
            <button
              type="button"
              data-testid="lead-drawer-questionnaire"
              onClick={() => setShowQuestionnaire(true)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#c9a961] bg-[#c9a961]/10 text-[#a3823f] px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
            >
              <ClipboardList className="h-3.5 w-3.5" />
              Business Health Questionnaire
            </button>
            <button
              type="button"
              data-testid="lead-drawer-convert"
              onClick={convert}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
              {lead.converted_client_id ? "Refresh CRM record" : "Add as client (push to CRM)"}
            </button>
            <button
              type="button"
              data-testid="lead-drawer-records-open"
              onClick={() => setMode("records")}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#1e3d3a]/15 bg-white text-[#1e3d3a] px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
            >
              <FileText className="h-3.5 w-3.5" />
              Request records
            </button>
            <button
              type="button"
              data-testid="lead-drawer-book-open"
              onClick={() => setMode("book")}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#1e3d3a]/15 bg-white text-[#1e3d3a] px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
            >
              <Calendar className="h-3.5 w-3.5" />
              Book appointment
            </button>
            <button
              type="button"
              data-testid="lead-drawer-call-open"
              onClick={() => setMode("call")}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#1e3d3a]/15 bg-white text-[#1e3d3a] px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
            >
              <PhoneCall className="h-3.5 w-3.5" />
              Log call outcome
            </button>
            {lead.phone && (
              <a
                href={`tel:${lead.phone}`}
                data-testid="lead-drawer-dial"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#1e3d3a]/15 bg-white text-[#1e3d3a]/70 px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
              >
                <Phone className="h-3.5 w-3.5" /> Dial {lead.phone}
              </a>
            )}
            <a
              href={`mailto:${lead.email}`}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#1e3d3a]/15 bg-white text-[#1e3d3a]/70 px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open in mail client
            </a>
          </div>
        )}

        {mode === "records" && (
          <div className="px-5 pb-8 space-y-3" data-testid="lead-drawer-records-panel">
            {/* Template picker — templates grouped by category (industry) so
                the picker scales past 10-15 saved lists. Users can filter by
                a category chip, or leave "All" to see every template. */}
            {templates.length > 0 && (
              <TemplatePicker
                templates={templates}
                loadedTemplateId={loadedTemplate?.id}
                onApply={applyTemplate}
                onDelete={deleteTemplate}
                onRecategorize={recategorizeTemplate}
                onClearLoaded={() => setLoadedTemplate(null)}
              />
            )}
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Records to request</p>
            <ul className="space-y-1.5">
              {DEFAULT_RECORDS.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <input
                    id={`rec-${item}`}
                    data-testid={`records-item-${item.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}
                    type="checkbox"
                    checked={!!checklist[item]}
                    onChange={(e) => setChecklist({ ...checklist, [item]: e.target.checked })}
                    className="mt-1 h-3.5 w-3.5 accent-[#1e3d3a]"
                  />
                  <label htmlFor={`rec-${item}`} className="text-xs text-[#1e3d3a] leading-relaxed cursor-pointer">
                    {item}
                  </label>
                </li>
              ))}
              {customItems.map((c, idx) => (
                <li key={`custom-${idx}`} className="flex items-start gap-2 group">
                  <input
                    id={`rec-custom-${idx}`}
                    data-testid={`records-custom-item-${idx}`}
                    type="checkbox"
                    checked={!!c.checked}
                    onChange={(e) => {
                      const next = [...customItems];
                      next[idx] = { ...next[idx], checked: e.target.checked };
                      setCustomItems(next);
                    }}
                    className="mt-1 h-3.5 w-3.5 accent-[#a3823f]"
                  />
                  <label htmlFor={`rec-custom-${idx}`} className="text-xs text-[#1e3d3a] leading-relaxed cursor-pointer flex-1">
                    <span className="inline-block mr-1.5 text-[9px] uppercase tracking-[0.22em] text-[#a3823f]">custom</span>
                    {c.text}
                  </label>
                  <button
                    type="button"
                    onClick={() => setCustomItems(customItems.filter((_, i) => i !== idx))}
                    className="text-[#1e3d3a]/30 hover:text-[#b23a48] opacity-0 group-hover:opacity-100 transition-all duration-300"
                    aria-label="Remove custom item"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
            {/* Add-custom row */}
            <div className="flex items-center gap-1.5 pt-2 border-t border-[#1e3d3a]/8">
              <input
                data-testid="lead-drawer-records-custom"
                type="text"
                value={newCustom}
                onChange={(e) => setNewCustom(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomItem(); } }}
                placeholder="Add another record request…"
                className="flex-1 text-xs rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 focus:border-[#c9a961] focus:outline-none"
              />
              <button
                type="button"
                data-testid="lead-drawer-records-custom-add"
                onClick={addCustomItem}
                disabled={!newCustom.trim()}
                className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-2.5 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-40 transition-colors duration-300"
                title="Append this line to the request"
              >
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
            <textarea
              data-testid="lead-drawer-records-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional personal note that appears inside the email…"
              className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-2 focus:border-[#c9a961] focus:outline-none resize-y"
            />
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <button
                type="button"
                data-testid="lead-drawer-records-send"
                onClick={requestRecords}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                Send request
              </button>
              {/* When a template is loaded, prefer "Update <name>" over "Save as new".
                  Founders can still create a new one with the secondary chip. */}
              {loadedTemplate ? (
                <>
                  <button
                    type="button"
                    data-testid="lead-drawer-records-update-template"
                    onClick={updateLoadedTemplate}
                    disabled={savingTemplate}
                    className="inline-flex items-center gap-1 rounded-full border border-[#c9a961] bg-[#c9a961]/12 text-[#a3823f] hover:bg-[#c9a961]/25 px-3 py-2 text-[10px] uppercase tracking-[0.22em] disabled:opacity-60 transition-colors duration-300"
                    title={`Overwrite “${loadedTemplate.name}” with the current checklist`}
                  >
                    {savingTemplate ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    <span className="max-w-[10rem] truncate">Update {loadedTemplate.name}</span>
                  </button>
                  <button
                    type="button"
                    data-testid="lead-drawer-records-save-template"
                    onClick={saveTemplate}
                    disabled={savingTemplate}
                    className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/20 bg-white text-[#1e3d3a]/70 hover:text-[#1e3d3a] hover:border-[#c9a961] px-3 py-2 text-[10px] uppercase tracking-[0.22em] disabled:opacity-60 transition-colors duration-300"
                    title="Save as a new template instead of overwriting"
                  >
                    Save as new
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  data-testid="lead-drawer-records-save-template"
                  onClick={saveTemplate}
                  disabled={savingTemplate}
                  className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/20 bg-white text-[#1e3d3a]/70 hover:text-[#1e3d3a] hover:border-[#c9a961] px-3 py-2 text-[10px] uppercase tracking-[0.22em] disabled:opacity-60 transition-colors duration-300"
                  title="Save this list as a reusable template"
                >
                  {savingTemplate ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  Save as template
                </button>
              )}
              <button
                type="button"
                onClick={() => setMode("view")}
                className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-3 py-2 transition-colors duration-300"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {mode === "book" && (
          <div className="px-5 pb-8 space-y-3" data-testid="lead-drawer-book-panel">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Schedule diagnostic</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs text-[#1e3d3a]">
                <span className="block text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1">Date</span>
                <input
                  type="date"
                  data-testid="lead-drawer-book-date"
                  value={bookDate}
                  onChange={(e) => setBookDate(e.target.value)}
                  className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-2 focus:border-[#c9a961] focus:outline-none"
                />
              </label>
              <label className="block text-xs text-[#1e3d3a]">
                <span className="block text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1">Time</span>
                <input
                  type="time"
                  data-testid="lead-drawer-book-time"
                  value={bookTime}
                  onChange={(e) => setBookTime(e.target.value)}
                  className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-2 focus:border-[#c9a961] focus:outline-none"
                />
              </label>
            </div>
            <label className="block text-xs text-[#1e3d3a]">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 flex items-center gap-1">
                <Globe className="h-3 w-3" /> Client's timezone
              </span>
              <select
                data-testid="lead-drawer-book-tz"
                value={bookTz}
                onChange={(e) => setBookTz(e.target.value)}
                className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-2 focus:border-[#c9a961] focus:outline-none"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
              <span className="block text-[10px] text-[#1e3d3a]/50 mt-1">
                The client sees this time in their timezone. We convert to UTC when saving.
              </span>
            </label>
            <label className="block text-xs text-[#1e3d3a]">
              <span className="block text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1">Goal (optional)</span>
              <textarea
                data-testid="lead-drawer-book-goal"
                rows={3}
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder={lead.message || "e.g. Walk through Q3 P&L, identify quick wins"}
                className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-2 focus:border-[#c9a961] focus:outline-none resize-y"
              />
            </label>
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                data-testid="lead-drawer-book-confirm"
                onClick={bookAppointment}
                disabled={busy || !bookDate || !bookTime}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calendar className="h-3.5 w-3.5" />}
                Confirm booking
              </button>
              <button
                type="button"
                onClick={() => setMode("view")}
                className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-3 py-2 transition-colors duration-300"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {mode === "call" && (
          <div className="px-5 pb-8 space-y-3" data-testid="lead-drawer-call-panel">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Log a call outcome</p>
            <div className="grid grid-cols-2 gap-1.5">
              {CALL_OUTCOMES.map((o) => {
                const Icon = o.icon;
                const active = callOutcome === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`call-outcome-${o.id}`}
                    onClick={() => setCallOutcome(o.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
                      active
                        ? "bg-[#1e3d3a] text-white ring-2 ring-[#c9a961]"
                        : `${o.tone} hover:brightness-95`
                    }`}
                  >
                    <Icon className="h-3 w-3" /> {o.label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">Duration (min)</span>
                <input
                  data-testid="call-duration"
                  type="number"
                  min="1"
                  max="240"
                  value={callDuration}
                  onChange={(e) => setCallDuration(e.target.value)}
                  placeholder="—"
                  className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-2 focus:border-[#c9a961] focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">Called by</span>
                <select
                  data-testid="call-by"
                  value={callBy}
                  onChange={(e) => {
                    setCallBy(e.target.value);
                    try { localStorage.setItem("elevare_transfer_approver", e.target.value); } catch {}
                  }}
                  className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-2 focus:border-[#c9a961] focus:outline-none"
                >
                  <option value="Kalib">Kalib</option>
                  <option value="John">John</option>
                  <option value="admin">Other</option>
                </select>
              </label>
            </div>
            <textarea
              data-testid="call-notes"
              rows={4}
              value={callNotes}
              onChange={(e) => setCallNotes(e.target.value)}
              placeholder="Notes — what did they say? Any next step? (optional)"
              className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2.5 py-2 focus:border-[#c9a961] focus:outline-none resize-y"
            />
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                data-testid="call-save"
                onClick={saveCallLog}
                disabled={busy || !callOutcome}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PhoneCall className="h-3.5 w-3.5" />}
                Save call log
              </button>
              <button
                type="button"
                onClick={() => setMode("view")}
                className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-3 py-2 transition-colors duration-300"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Call history — always visible in view mode when there are logs */}
        {mode === "view" && (lead.call_logs?.length > 0) && (
          <div className="px-5 pb-8" data-testid="lead-drawer-call-history">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] mb-2">Call history</p>
            <ul className="space-y-2">
              {(lead.call_logs || []).slice(0, 8).map((c) => {
                const meta = CALL_OUTCOMES.find((o) => o.id === c.outcome);
                const Icon = meta?.icon || PhoneCall;
                return (
                  <li
                    key={c.id || c.at}
                    data-testid={`call-log-${c.id || c.at}`}
                    className="rounded-lg border border-[#1e3d3a]/10 bg-white px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.22em] ${meta?.tone || "bg-black/8 text-black/55"}`}>
                        <Icon className="h-3 w-3" /> {meta?.label || c.outcome}
                      </span>
                      <span className="text-[10px] text-[#1e3d3a]/55">
                        {fmtWhen(c.at)} · by {c.by || "admin"}
                        {c.duration_min ? ` · ${c.duration_min} min` : ""}
                      </span>
                    </div>
                    {c.notes && (
                      <p className="text-xs text-[#1e3d3a]/75 leading-relaxed whitespace-pre-wrap">{c.notes}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </aside>

      {showQuestionnaire && (
        <HealthQuestionnaire
          leadId={lead.id}
          lead={lead}
          onClose={() => setShowQuestionnaire(false)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// TemplatePicker — grouped, filterable pill library
// -----------------------------------------------------------------------------
// Groups saved templates by their category (industry tag) and shows a chip row
// at the top so a founder with many saved lists can filter to just the ones
// relevant to the lead in front of them. Uncategorised templates fall under
// "general". Selecting a chip narrows the visible pills; selecting "All" shows
// everything grouped by category.
function TemplatePicker({ templates, loadedTemplateId, onApply, onDelete, onRecategorize, onClearLoaded }) {
  const [filter, setFilter] = useState("__all__");

  const grouped = templates.reduce((acc, t) => {
    const key = (t.category || "general").trim().toLowerCase() || "general";
    (acc[key] ||= []).push(t);
    return acc;
  }, {});
  const categoryKeys = Object.keys(grouped).sort();
  const visible = filter === "__all__"
    ? categoryKeys
    : categoryKeys.filter((k) => k === filter);
  const loadedTpl = templates.find((t) => t.id === loadedTemplateId);

  return (
    <div data-testid="records-template-picker" className="rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/6 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] flex items-center gap-1.5">
          <BookMarked className="h-3 w-3" /> Load a template
        </p>
        {categoryKeys.length > 1 && (
          <div className="flex flex-wrap gap-1" data-testid="records-template-filter">
            <FilterChip active={filter === "__all__"} onClick={() => setFilter("__all__")}>
              All
              <span className="text-[9px] uppercase tracking-[0.18em] opacity-60 ml-1">
                {templates.length}
              </span>
            </FilterChip>
            {categoryKeys.map((cat) => (
              <FilterChip
                key={cat}
                testid={`records-template-filter-${cat}`}
                active={filter === cat}
                onClick={() => setFilter(cat)}
              >
                {cat}
                <span className="text-[9px] uppercase tracking-[0.18em] opacity-60 ml-1">
                  {grouped[cat].length}
                </span>
              </FilterChip>
            ))}
          </div>
        )}
      </div>
      {loadedTpl && (
        <p data-testid="records-template-loaded-note" className="text-[10px] text-[#1e3d3a]/60 flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#c9a961]" />
          Editing <span className="font-medium text-[#1e3d3a] max-w-[12rem] truncate">{loadedTpl.name}</span>
          <button
            type="button"
            data-testid="records-template-loaded-clear"
            onClick={onClearLoaded}
            className="text-[#1e3d3a]/40 hover:text-[#1e3d3a] transition-colors duration-300 underline decoration-dotted"
          >
            start fresh
          </button>
        </p>
      )}
      <div className="space-y-2">
        {visible.map((cat) => (
          <div key={cat} data-testid={`records-template-group-${cat}`}>
            {filter === "__all__" && categoryKeys.length > 1 && (
              <p className="text-[9px] uppercase tracking-[0.24em] text-[#1e3d3a]/40 mb-1">{cat}</p>
            )}
            <div className="flex flex-wrap gap-1.5">
              {grouped[cat].map((tpl) => {
                const isLoaded = tpl.id === loadedTemplateId;
                return (
                <div key={tpl.id} className={`group inline-flex items-center gap-1 rounded-full pl-3 pr-1 py-0.5 transition-colors duration-300 ${
                  isLoaded
                    ? "bg-[#c9a961]/25 border border-[#c9a961]"
                    : "bg-white border border-[#1e3d3a]/12"
                }`}>
                  <button
                    type="button"
                    data-testid={`records-template-apply-${tpl.id}`}
                    onClick={() => onApply(tpl)}
                    className={`text-[11px] transition-colors duration-300 ${
                      isLoaded ? "text-[#a3823f] font-medium" : "text-[#1e3d3a] hover:text-[#a3823f]"
                    }`}
                    title={`${tpl.items?.length || 0} items · ${tpl.name}`}
                  >
                    {tpl.name}
                    <span className="text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/40 ml-1.5">
                      {tpl.items?.length || 0}
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid={`records-template-recategorize-${tpl.id}`}
                    onClick={() => onRecategorize(tpl)}
                    className="rounded-full p-1 text-[#1e3d3a]/30 hover:text-[#a3823f] opacity-0 group-hover:opacity-100 transition-all duration-300"
                    aria-label={`Move ${tpl.name} to another category`}
                    title="Move to another category"
                  >
                    <BookMarked className="h-2.5 w-2.5" />
                  </button>
                  <button
                    type="button"
                    data-testid={`records-template-delete-${tpl.id}`}
                    onClick={() => onDelete(tpl)}
                    className="rounded-full p-1 text-[#1e3d3a]/30 hover:text-[#b23a48] opacity-0 group-hover:opacity-100 transition-all duration-300"
                    aria-label={`Delete template ${tpl.name}`}
                  >
                    <Trash2 className="h-2.5 w-2.5" />
                  </button>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border transition-colors duration-300 ${
        active
          ? "bg-[#1e3d3a] text-white border-[#1e3d3a]"
          : "bg-transparent text-[#1e3d3a]/60 border-[#1e3d3a]/15 hover:border-[#c9a961] hover:text-[#1e3d3a]"
      }`}
    >
      {children}
    </button>
  );
}
